import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SOCIALS } from "@/lib/socials";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Waitlist() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((res) => setCount(res.data.count))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/waitlist`, { email });
      if (res.data.status === "invalid") {
        toast.error(res.data.message);
        setStatus("idle");
        return;
      }
      if (res.data.status === "exists") {
        toast.info(res.data.message);
      }
      if (res.data.count) setCount(res.data.count);
      setStatus("success");
    } catch {
      toast.error("Something went wrong. Please try again.");
      setStatus("idle");
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="border-t border-white/10 px-6 md:px-12 py-32 md:py-48 text-center"
    >
      <motion.div
        initial={{ opacity: 0, y: 48 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-3xl mx-auto"
      >
        <p className="font-mono-accent text-xs sm:text-sm uppercase tracking-[0.3em] text-[#8B99AE] mb-8">
          Early access for shift workers
        </p>
        <h2
          data-testid="waitlist-heading"
          className="font-serif-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none"
        >
          RESERVE YOUR <span className="text-[#4ADE80]">PLACE.</span>
        </h2>
        <p className="mt-6 text-sm sm:text-base leading-relaxed text-[#8B99AE] max-w-lg mx-auto">
          Nurses, tradies, paramedics, hospitality crews — be first to try the
          go-to app for shift workers.
        </p>

        {status === "success" ? (
          <motion.div
            data-testid="waitlist-success-message"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-14 flex flex-col items-center gap-4"
          >
            <span className="flex items-center justify-center w-12 h-12 border border-white/25">
              <Check className="w-5 h-5" />
            </span>
            <p className="font-serif-display italic text-2xl sm:text-3xl">
              You are on the list.
            </p>
            <p className="text-sm text-[#8B99AE]">
              We'll write to you the moment Rosteno is ready.
            </p>
            <div className="mt-10 flex flex-col items-center gap-4">
              <span className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#8B99AE]">
                Follow the build
              </span>
              <div
                data-testid="waitlist-social-links"
                className="flex items-center gap-4"
              >
                {SOCIALS.map((social) => (
                  <a
                    key={social.name}
                    data-testid={`waitlist-social-${social.name.toLowerCase()}`}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${social.name} — ${social.handle}`}
                    title={`${social.name} — ${social.handle}`}
                    className="flex items-center justify-center w-11 h-11 rounded-full border border-white/15 text-[#8B99AE] transition-colors duration-300 hover:border-[#4ADE80] hover:text-[#4ADE80]"
                  >
                    <svg viewBox="0 0 24 24" width="17" height="17" className="fill-current" aria-hidden="true">
                      <path d={social.path} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          <form
            data-testid="waitlist-form"
            onSubmit={submit}
            className="mt-14 flex flex-col sm:flex-row items-stretch gap-6 max-w-xl mx-auto"
          >
            <input
              data-testid="waitlist-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent border-0 border-b border-white/25 focus:border-white rounded-none px-0 py-4 text-base placeholder:text-[#8B99AE]/50 focus:outline-none transition-colors duration-300"
            />
            <button
              data-testid="waitlist-submit-button"
              type="submit"
              disabled={status === "loading"}
              className="group flex items-center justify-center gap-3 font-mono-accent text-xs uppercase tracking-[0.2em] rounded-full bg-gradient-to-r from-[#34D399] to-[#38BDF8] text-black px-8 py-4 transition-opacity duration-300 hover:opacity-85 disabled:opacity-50"
            >
              {status === "loading" ? "Joining..." : "Join Waitlist"}
              <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        )}

        <p
          data-testid="waitlist-count"
          className="mt-10 font-mono-accent text-xs tracking-[0.2em] uppercase text-[#8B99AE]"
        >
          {count !== null
            ? `— ${count} ${count === 1 ? "person" : "people"} already waiting`
            : "— Be the first to know"}
        </p>
      </motion.div>
    </section>
  );
}
